import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../redux/store";
import {
  declineFoodItem,
  approveFoodItem,
  fetchReviewRequestPosts,
  fetchReviewRequestPostsSearch,
} from "../../redux/slices/reviewRequestSlice";
import {
  fetchDailyOffers,
  approveDailyOffer,
  declineDailyOffer,
  fetchDailyOffersSearch,
} from "../../redux/slices/dailyOfferSlice";
import DeleteButton from "../../components/DeleteButton/DeleteButton";
import CheckButton from "../../components/CheckButton/CheckButton";
import { CiSearch } from "react-icons/ci";
import { IoIosArrowBack, IoIosArrowForward } from "react-icons/io";
import { ToastContainer } from "react-toastify";
import { Spin, notification } from "antd";
import { useTranslation } from "react-i18next";
import {
  approveRestaurant,
  declineRestaurant,
  fetchRestaurants,
  fetchRestaurantsStatus,
  setPage as setRestaurantPage,
} from "../../redux/slices/allRestaurantsSlice";

type Props = {};

type ActiveView = "reviewRequests" | "dailyOffers" | "users";

const ReviewRequest: React.FC = (props: Props) => {
  const dispatch = useDispatch<AppDispatch>();

  const { data, totalPages, page, loading, error } = useSelector(
    (state: RootState) => state.reviewRequest
  );

  const {
    data: dailyOfferData,
    loading: offersLoading,
    error: offersError,
    page: dailyOfferPage,
    totalPages: dailyOfferTotalPage,
  } = useSelector((state: RootState) => state.dailyOffer);

  // Selectors for Users (Assuming a users slice exists)
  // const {
  //   users: usersData,
  //   loading: usersLoading,
  //   error: usersError,
  //   page: usersPage,
  //   totalPages: usersTotalPages,
  // } = useSelector((state: RootState) => state.allUsers);

  const {
    restaurants: usersData,
    loading: usersLoading,
    error: usersError,
    page: usersPage,
    totalPages: usersTotalPages,
  } = useSelector((state: RootState) => state.restaurants);

  const { t } = useTranslation();

  const [activeView, setActiveView] = useState<ActiveView>("reviewRequests");
  const [searchTerm, setSearchTerm] = useState("");

  const handleApprove = (foodItemId: number) => {
    dispatch(approveFoodItem(foodItemId));
  };

  const handleDecline = (foodItemId: number) => {
    dispatch(declineFoodItem(foodItemId));
  };

  const handleApproveOffer = (dailyOfferId: number) => {
    dispatch(approveDailyOffer(dailyOfferId));
  };

  const handleDeclineOffer = (dailyOfferId: number) => {
    dispatch(declineDailyOffer(dailyOfferId));
  };

  const handleFetchReviewRequests = () => {
    setActiveView("reviewRequests");
    setSearchTerm("");
    dispatch(fetchReviewRequestPosts(0));
  };

  const handleFetchDailyOffers = () => {
    setActiveView("dailyOffers");
    setSearchTerm("");
    dispatch(fetchDailyOffers(0));
  };

  const handleFetchUsers = () => {
    setActiveView("users");
    setSearchTerm("");
    dispatch(setRestaurantPage(0));
    dispatch(
      fetchRestaurantsStatus({
        role: "RESTAURANT_ADMIN",
        page: 0,
        size: 5,
        name: "",
      })
    );
  };

  useEffect(() => {
    if (activeView === "reviewRequests") {
      dispatch(fetchReviewRequestPosts(0));
    } else if (activeView === "dailyOffers") {
      dispatch(fetchDailyOffers(0));
    } else if (activeView === "users") {
      dispatch(
        fetchRestaurantsStatus({
          role: "RESTAURANT_ADMIN",
          page: usersPage,
          size: 5,
        })
      );
    }
  }, [dispatch, activeView, usersPage]);

  useEffect(() => {
    if (searchTerm.trim()) {
      if (activeView === "dailyOffers") {
        dispatch(fetchDailyOffersSearch(searchTerm));
      } else if (activeView === "reviewRequests") {
        dispatch(fetchReviewRequestPostsSearch(searchTerm));
      } else if (activeView === "users") {
        // Dispatch action to fetch users
        dispatch(
          fetchRestaurantsStatus({
            role: "RESTAURANT_ADMIN",
            page: 0,
            size: 5,
            name: searchTerm,
          })
        );
      }
    } else {
      if (activeView === "dailyOffers") {
        dispatch(fetchDailyOffers(0));
      } else if (activeView === "reviewRequests") {
        dispatch(fetchReviewRequestPosts(0));
      } else if (activeView === "users") {
        dispatch(
          fetchRestaurantsStatus({
            role: "RESTAURANT_ADMIN",
            page: usersPage,
            size: 5,
          })
        );
      }
    }
  }, [dispatch, searchTerm, activeView]);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  const getPageRange = (currentPage: number, totalPages: number) => {
    const range = [];
    const visiblePages = 4; // Show 4 pages in the middle
    const half = Math.floor(visiblePages / 2);

    let start = currentPage - half;
    let end = currentPage + half;

    if (start < 0) {
      start = 0;
      end = Math.min(visiblePages - 1, totalPages - 1);
    }

    if (end >= totalPages) {
      end = totalPages - 1;
      start = Math.max(0, totalPages - visiblePages);
    }

    for (let i = start; i <= end; i++) {
      range.push(i);
    }

    return range;
  };

  // Pagination handlers and ranges for each view
  const handlePageChange = (newPage: number) => {
    if (newPage >= 0 && newPage < totalPages) {
      dispatch(fetchReviewRequestPosts(newPage));
    }
  };
  const pageRange = getPageRange(page, totalPages);

  const handleDailyOfferPageChange = (newPage: number) => {
    if (newPage >= 0 && newPage < dailyOfferTotalPage) {
      dispatch(fetchDailyOffers(newPage));
    }
  };

  const getDailyOfferPageRange = (currentPage: number, totalPages: number) => {
    const range = [];
    const visiblePages = 4; // Adjust if needed
    const half = Math.floor(visiblePages / 2);

    let start = currentPage - half;
    let end = currentPage + half;

    if (start < 0) {
      start = 0;
      end = Math.min(visiblePages - 1, totalPages - 1);
    }

    if (end >= totalPages) {
      end = totalPages - 1;
      start = Math.max(0, totalPages - visiblePages);
    }

    for (let i = start; i <= end; i++) {
      range.push(i);
    }

    return range;
  };

  const dailyOfferPageRange = getDailyOfferPageRange(
    dailyOfferPage,
    dailyOfferTotalPage
  ); // Keep this function name consistent

  const handleUsersPageChange = (newPage: number) => {
    if (newPage >= 0 && newPage < usersTotalPages) {
      dispatch(setRestaurantPage(newPage));
      // Dispatch action to fetch data for the new page
      dispatch(
        fetchRestaurantsStatus({
          role: "RESTAURANT_ADMIN",
          page: newPage,
          size: 5,
          name: searchTerm, 
        })
      );
    }
  };

  const usersPageRange = getPageRange(usersPage, usersTotalPages);

  const handleApproveRestaurant = async (id: number) => {
    try {
      await dispatch(approveRestaurant(id)).unwrap();
      notification.success({
        message: t("notification.success"), 
        description: t("reviewRequests.restaurantApprovedSuccess"), 
        placement: "topRight",
      });

      dispatch(
        fetchRestaurantsStatus({
          role: "RESTAURANT_ADMIN",
          page: usersPage, 
          size: 5,
          name: searchTerm,
        })
      );
    } catch (error: any) {
      notification.error({
        message: t("notification.error"),
        description:
          error.message || t("reviewRequests.restaurantApprovedError"),
        placement: "topRight",
      });
    }
  };

  const handleDeclineRestaurant = async (id: number) => {
    try {
      await dispatch(declineRestaurant(id)).unwrap();
      notification.success({
        message: t("notification.success"),
        description: t("reviewRequests.restaurantDeclinedSuccess"),
        placement: "topRight",
      });
      dispatch(
        fetchRestaurantsStatus({
          role: "RESTAURANT_ADMIN",
          page: usersPage,
          size: 5,
          name: searchTerm,
        })
      );
    } catch (error: any) {
      notification.error({
        message: t("notification.error"),
        description:
          error.message || t("reviewRequests.restaurantDeclinedError"), // Use error message or a default translation
        placement: "topRight",
      });
    }
  };

  return (
    <div>
      <ToastContainer />
      <div className="flex gap-2 mt-12">
        <button
          onClick={handleFetchReviewRequests}
          className={`w-[155px] h-[36px] justify-center flex items-center rounded-lg text-sm ${
            activeView === "reviewRequests" ? "bg-customBrown text-white" : ""
          }`}
        >
          {t("reviewRequests.listAllPosts")}
        </button>
        <button
          onClick={handleFetchDailyOffers}
          className={`w-[170px] h-[36px] justify-center flex items-center rounded-lg text-sm ${
            activeView === "dailyOffers" ? "bg-customBrown text-white" : ""
          }`}
        >
          {t("reviewRequests.listDailyOffers")}
        </button>
        <button
          onClick={handleFetchUsers} // Added onClick handler
          className={`w-[170px] h-[36px] justify-center flex items-center rounded-lg text-sm ${
            activeView === "users" ? "bg-customBrown text-white" : ""
          }`}
        >
          {t("reviewRequests.listOfUsers")}
        </button>
      </div>
      <div>
        <div className="flex items-end justify-between mt-16">
          <div>
            <h1 className="text-2xl font-medium">
              {t("reviewRequests.reviewRequest")}
            </h1>
            <span className="text-sm block">
              
              {/* Changed to span with block display */}
              {t("reviewRequests.reviewRequestDescription")}
            </span>
          </div>
          <div className="relative w-[320px]">
            <input
              type="search"
              className="w-full h-[40px] border-2 rounded-lg pl-9 text-sm pr-2 outline-none leading-[48px]"
              placeholder={t("reviewRequests.searchPlaceholder")}
              value={searchTerm}
              onChange={handleSearchChange}
            />
            <CiSearch
              className="absolute text-gray-400 transform -translate-y-1/2 left-3 top-1/2"
              size={16}
            />
          </div>
        </div>

        {/* Conditional rendering based on activeView */}
        {activeView === "dailyOffers" ? (
          // Daily Offers Table
          <div>
            <table className="w-full mt-12 table-auto">
              <thead>
                <tr className="h-[40px] border-b w-full">
                  <th className="pl-6 text-sm font-medium text-left text-gray-500 border-r-2">
                    {t("reviewRequests.foodImage")}
                  </th>
                  <th className="pl-6 text-sm font-medium text-left text-gray-500 border-r-2">
                    {t("reviewRequests.foodName")}
                  </th>
                  <th className="pl-6 text-sm font-medium text-left text-gray-500 border-r-2">
                    {t("reviewRequests.restaurantName")}
                  </th>
                  <th className="pl-6 text-sm font-medium text-left text-gray-500 border-r-2">
                    {t("reviewRequests.price")}
                  </th>
                  <th className="pl-6 text-sm font-medium text-left text-gray-500 border-r-2">
                    {t("reviewRequests.contactNumber")}
                  </th>
                  <th className="pl-6 text-sm font-medium text-left text-gray-500 border-r-2">
                    {t("reviewRequests.location")}
                  </th>
                  <th className="w-[136px]"></th>
                </tr>
              </thead>
              <tbody>
                {offersLoading ? (
                  <tr className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
                    <td
                      colSpan={7}
                      className="flex justify-center items-center"
                    >
                      <Spin size="large" />
                    </td>
                  </tr>
                ) : offersError ? (
                  <tr>
                    <td colSpan={7}>{t("reviewRequests.noOffersFound")}</td>
                  </tr>
                ) : dailyOfferData.length === 0 ? (
                  <tr>
                    <td colSpan={7}>{t("reviewRequests.noOffersFound")}</td>
                  </tr>
                ) : (
                  dailyOfferData.map((offer, index) => (
                    <tr
                      key={offer.id}
                      className={`h-[56px] border-b ${
                        index % 2 === 0 ? "bg-gray-50" : "bg-white"
                      }`}
                    >
                      <td className="pl-6 text-sm font-normal text-left border-r-2">
                        <img
                          src={offer.imageUrl}
                          alt={offer.title}
                          className="h-[36px] w-[43px]"
                        />
                      </td>
                      <td className="pl-6 text-sm font-normal text-left border-r-2">
                        {offer.title}
                      </td>
                      <td className="pl-6 text-sm font-normal text-left border-r-2">
                        {offer.restaurantName}
                      </td>
                      <td className="pl-6 text-sm font-normal text-left border-r-2">
                        ${offer.price.toFixed(2)}
                      </td>
                      <td className="pl-6 text-sm font-normal text-left border-r-2">
                        {offer.contactNumber}
                      </td>
                      <td className="pl-6 text-sm font-normal text-left border-r-2">
                        {offer.location}
                      </td>
                      <td className="w-[136px]">
                        <div className="flex justify-center gap-2 px-5">
                          <CheckButton
                            onClick={() => handleApproveOffer(offer.id)}
                          />
                          <DeleteButton
                            color="#D52222"
                            onClick={() => handleDeclineOffer(offer.id)}
                          />
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
            {/* Pagination for Daily Offers */}
            <div className="flex justify-start gap-2 mt-4 pagination-controls">
              <button
                onClick={() => handleDailyOfferPageChange(dailyOfferPage - 1)}
                disabled={dailyOfferPage === 0}
                className="border rounded-lg disabled:opacity-50 disabled:bg-[#E9ECEF] w-[40px] h-[40px] flex justify-center items-center"
              >
                <IoIosArrowBack size={10} />
              </button>
              {dailyOfferPageRange.map((p) => (
                <button
                  key={p}
                  onClick={() => handleDailyOfferPageChange(p)}
                  className={`w-[40px] h-[40px] flex justify-center items-center border rounded-lg text-sm ${
                    p === dailyOfferPage ? "border-black" : ""
                  }`}
                >
                  {p + 1}
                </button>
              ))}
              <button
                onClick={() => handleDailyOfferPageChange(dailyOfferPage + 1)}
                disabled={dailyOfferPage + 1 >= dailyOfferTotalPage} // Corrected total pages check
                className="border rounded-lg disabled:opacity-50 disabled:bg-[#E9ECEF] w-[40px] h-[40px] flex justify-center items-center"
              >
                <IoIosArrowForward size={10} />
              </button>
            </div>
          </div>
        ) : activeView === "users" ? (
          // Users Table (Example structure - adjust based on your user data)
          <div>
            <table className="w-full mt-12 table-auto">
              <thead>
                <tr className="h-[40px] border-b w-full">
                  <th className="pl-6 text-sm font-medium text-left text-gray-500 border-r-2">
                    {t("reviewRequests.name")}
                  </th>
                  <th className="pl-6 text-sm font-medium text-left text-gray-500 border-r-2">
                    {t("reviewRequests.emailAddress")}
                  </th>
                  <th className="pl-6 text-sm font-medium text-left text-gray-500 border-r-2">
                    {t("reviewRequests.phoneNumber")}
                  </th>
                  <th className="pl-6 text-sm font-medium text-left text-gray-500 border-r-2">
                    {t("reviewRequests.adminName")}
                  </th>
                  <th className="pl-6 text-sm font-medium text-left text-gray-500 border-r-2">
                    {t("reviewRequests.ownerName")}
                  </th>
                  <th className="pl-6 text-sm font-medium text-left text-gray-500 border-r-2">
                    {t("reviewRequests.ownerEmail")}
                  </th>
                  <th className="pl-6 text-sm font-medium text-left text-gray-500 border-r-2">
                    {t("reviewRequests.openingHours")}
                  </th>
                  <th className="pl-6 text-sm font-medium text-left text-gray-500 border-r-2">
                    {t("reviewRequests.category")}
                  </th>
                  <th className="pl-6 text-sm font-medium text-left text-gray-500 border-r-2">
                    {t("reviewRequests.location")}
                  </th>
                  <th className="w-[136px]"></th>
                </tr>
              </thead>
              <tbody>
                {usersLoading ? (
                  <tr>
                    <td colSpan={10} className="text-center py-4">
                      <Spin size="large" />
                    </td>
                  </tr>
                ) : usersError ? (
                  <tr>
                    <td colSpan={10} className="text-center py-4 text-red-500">
                      {t("reviewRequests.noUsersFound")}
                    </td>
                  </tr>
                ) : usersData.length === 0 ? (
                  <tr>
                    <td colSpan={10} className="text-center py-4">
                      {t("reviewRequests.noUsersFound")}
                    </td>
                  </tr>
                ) : (
                  usersData.map((user, index) => (
                    <tr
                      key={user.id}
                      className={`h-[56px] border-b ${
                        index % 2 === 0 ? "bg-gray-50" : "bg-white"
                      }`}
                    >
                      {/* Remove line breaks between td elements */}
                      <td className="pl-6 text-sm font-normal text-left border-r-2">
                        {user.name}
                      </td>
                      <td className="pl-6 text-sm font-normal text-left border-r-2">
                        {user.emailAddress}
                      </td>
                      <td className="pl-6 text-sm font-normal text-left border-r-2">
                        {user.phoneNumber}
                      </td>
                      <td className="pl-6 text-sm font-normal text-left border-r-2">
                        {user.adminName}
                      </td>
                      <td className="pl-6 text-sm font-normal text-left border-r-2">
                        {user.ownerName}
                      </td>
                      <td className="pl-6 text-sm font-normal text-left border-r-2">
                        {user.ownerEmail}
                      </td>
                      <td className="pl-6 text-sm font-normal text-left border-r-2">
                        {user.openingHours}
                      </td>
                      <td className="pl-6 text-sm font-normal text-left border-r-2">
                        {user.category}
                      </td>
                      <td className="pl-6 text-sm font-normal text-left border-r-2">
                        {user.location}
                      </td>
                      <td className="w-[136px] px-5">
                        <div className="flex justify-center gap-2">
                          <CheckButton
                            onClick={() => handleApproveRestaurant(user.id)}
                          />
                          <DeleteButton
                            color="#D52222"
                            onClick={() => handleDeclineRestaurant(user.id)}
                          />
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
            {/* Pagination for Users */}
            <div className="flex justify-start gap-2 mt-4 pagination-controls">
              <button
                onClick={() => handleUsersPageChange(usersPage - 1)}
                disabled={usersPage === 0}
                className="border rounded-lg disabled:opacity-50 disabled:bg-[#E9ECEF] w-[40px] h-[40px] flex justify-center items-center"
              >
                <IoIosArrowBack size={14} />
              </button>

              {/* THIS IS THE PART THAT RENDERS THE PAGE NUMBERS */}
              {usersPageRange.map(
                (
                  p 
                ) => (
                  <button
                    key={p}
                    onClick={() => handleUsersPageChange(p)}
                    className={`w-[40px] h-[40px] flex justify-center items-center border rounded-lg text-sm ${

                      p === usersPage ? "border-black" : ""
                    }`}
                  >
                    {p + 1}

                  </button>
                )
              )}

              <button
                onClick={() => handleUsersPageChange(usersPage + 1)}
                disabled={usersPage + 1 >= usersTotalPages}
                className="border rounded-lg disabled:opacity-50 disabled:bg-[#E9ECEF] w-[40px] h-[40px] flex justify-center items-center"
              >
                <IoIosArrowForward size={14} />
              </button>
            </div>
          </div>
        ) : (
          // Review Requests Table (Default view)
          <div>
            <table className="w-full mt-12 table-auto">
              <thead>
                <tr className="h-[40px] border-b w-full">
                  <th className="pl-6 text-sm font-medium text-left text-gray-500 border-r-2 ">
                    {t("reviewRequests.foodImage")}
                  </th>
                  <th className="pl-6 text-sm font-medium text-left text-gray-500 border-r-2 ">
                    {t("reviewRequests.foodName")}
                  </th>
                  <th className="pl-6 text-sm font-medium text-left text-gray-500 border-r-2 ">
                    {t("reviewRequests.restaurantName")}
                  </th>
                  <th className="pl-6 text-sm font-medium text-left text-gray-500 border-r-2">
                    {t("reviewRequests.price")}
                  </th>
                  <th className="pl-6 text-sm font-medium text-left text-gray-500 border-r-2 ">
                    {t("reviewRequests.category")}
                  </th>
                  <th className="pl-6 text-sm font-medium text-left text-gray-500 border-r-2 ">
                    {t("reviewRequests.contactNumber")}
                  </th>
                  <th className="pl-6 text-sm font-medium text-left text-gray-500 border-r-2 ">
                    {t("reviewRequests.location")}
                  </th>
                  <th className="w-[136px]"></th>
                  {/* Empty header for actions */}
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
                    <td
                      colSpan={8}
                      className="flex justify-center items-center"
                    >
                      <Spin size="large" />
                    </td>
                  </tr>
                ) : error ? (
                  <tr>
                    <td colSpan={8}>Error: {error}</td>
                    {/* Corrected colspan */}
                  </tr>
                ) : data.length === 0 ? (
                  <tr>
                    <td colSpan={8}>{t("reviewRequests.noPostsFound")}</td>
                    {/* Corrected colspan */}
                  </tr>
                ) : (
                  data.map((post, index) => (
                    <tr
                      className={`h-[56px] border-b ${
                        index % 2 === 0 ? "bg-gray-50" : "bg-white"
                      }`}
                      key={post.id}
                    >
                      <td className="pl-6 text-sm font-normal text-left border-r-2">
                        <img
                          src={post.imageUrl}
                          alt={post.foodName}
                          className="h-[36px] w-[43px]"
                        />
                      </td>
                      <td className="pl-6 text-sm font-normal text-left border-r-2">
                        {post.foodName}
                      </td>
                      <td className="pl-6 text-sm font-normal text-left border-r-2">
                        {post.restaurantName}
                      </td>
                      <td className="pl-6 text-sm font-normal text-left border-r-2">
                        ${post.price.toFixed(2)}
                      </td>
                      <td className="pl-6 text-sm font-normal text-left border-r-2">
                        {post.categories.join(", ")}
                      </td>
                      <td className="pl-6 text-sm font-normal text-left border-r-2">
                        {post.contactNumber}
                      </td>
                      <td className="pl-6 text-sm border-r-2">
                        {post.location}
                      </td>
                      <td className="w-[136px]">
                        <div className="flex justify-center gap-2 px-5">
                          <CheckButton onClick={() => handleApprove(post.id)} />
                          <DeleteButton
                            color="#D52222"
                            onClick={() => handleDecline(post.id)}
                          />
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
            {/* Pagination for Review Requests */}
            <div className="flex justify-start gap-2 mt-4 pagination-controls">
              <button
                onClick={() => handlePageChange(page - 1)}
                disabled={page === 0}
                className="border rounded-lg disabled:opacity-50 disabled:bg-[#E9ECEF] w-[40px] h-[40px] flex justify-center items-center"
              >
                <IoIosArrowBack size={10} />
              </button>
              {pageRange.map((p) => (
                <button
                  key={p}
                  onClick={() => handlePageChange(p)}
                  className={`w-[40px] h-[40px] flex justify-center items-center border rounded-lg text-sm ${
                    p === page ? "border-black" : ""
                  }`}
                >
                  {p + 1}
                </button>
              ))}
              <button
                onClick={() => handlePageChange(page + 1)}
                disabled={page + 1 >= totalPages}
                className="border rounded-lg disabled:opacity-50 disabled:bg-[#E9ECEF] w-[40px] h-[40px] flex justify-center items-center"
              >
                <IoIosArrowForward size={10} />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ReviewRequest;
